import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
    XMarkIcon,
    MagnifyingGlassIcon,
    AcademicCapIcon,
    BookOpenIcon,
    MapPinIcon
} from '@heroicons/react/24/outline'
import { universityAPI, courseAPI } from '../../services/api'
import LoadingSpinner from '../Common/LoadingSpinner'

const SearchModal = ({ isOpen, onClose }) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [searchType, setSearchType] = useState('all') // all, universities, courses

    const { data: universities, isLoading: universitiesLoading } = useQuery({
        queryKey: ['search-universities', searchQuery],
        queryFn: () => universityAPI.getUniversities({ search: searchQuery, limit: 10 }),
        enabled: isOpen && (searchType === 'all' || searchType === 'universities') && searchQuery.length > 2,
    })

    const { data: courses, isLoading: coursesLoading } = useQuery({
        queryKey: ['search-courses', searchQuery],
        queryFn: () => courseAPI.getCourses({ search: searchQuery, limit: 10 }),
        enabled: isOpen && (searchType === 'all' || searchType === 'courses') && searchQuery.length > 2,
    })

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'unset'
        }

        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [isOpen])

    const handleClose = () => {
        setSearchQuery('')
        setSearchType('all')
        onClose()
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleClose()
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-start justify-center min-h-screen px-4 pt-16 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75"
                        onClick={handleClose}
                    />

                    {/* Modal panel */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -20 }}
                        transition={{ duration: 0.2 }}
                        className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl relative z-10"
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-gray-900">
                                Search
                            </h3>
                            <button
                                onClick={handleClose}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 relative z-20"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="relative mb-6">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-accent-500 focus:border-accent-500 text-lg"
                                placeholder="Search universities, courses..."
                                autoFocus
                            />
                        </div>

                        {/* Search Type Tabs */}
                        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setSearchType('all')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 ${searchType === 'all'
                                    ? 'bg-white text-accent-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setSearchType('universities')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 ${searchType === 'universities'
                                    ? 'bg-white text-accent-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Universities
                            </button>
                            <button
                                onClick={() => setSearchType('courses')}
                                className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors duration-200 ${searchType === 'courses'
                                    ? 'bg-white text-accent-600 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Courses
                            </button>
                        </div>

                        {/* Search Results */}
                        <div className="max-h-96 overflow-y-auto">
                            {searchQuery.length < 3 ? (
                                <div className="text-center py-8">
                                    <MagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500">Type at least 3 characters to search</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Universities Results */}
                                    {(searchType === 'all' || searchType === 'universities') && (
                                        <div>
                                            <div className="flex items-center space-x-2 mb-3">
                                                <AcademicCapIcon className="w-5 h-5 text-gray-600" />
                                                <h4 className="font-semibold text-gray-900">Universities</h4>
                                                {universitiesLoading && <LoadingSpinner size="sm" />}
                                            </div>

                                            {universities?.data?.universities?.length > 0 ? (
                                                <div className="space-y-2">
                                                    {universities.data.universities.map((university) => (
                                                        <Link
                                                            key={university.id}
                                                            to={`/universities/${university.slug}`}
                                                            onClick={handleClose}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            <div className="flex items-start space-x-3">
                                                                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                    <AcademicCapIcon className="w-5 h-5 text-primary-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h5 className="font-medium text-gray-900 truncate">
                                                                        {university.name}
                                                                    </h5>
                                                                    <div className="flex items-center text-sm text-gray-500 mt-1">
                                                                        <MapPinIcon className="w-4 h-4 mr-1" />
                                                                        <span>{university.city}, {university.state}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    ))}
                                                </div>
                                            ) : !universitiesLoading && (
                                                <p className="text-gray-500 text-sm">No universities found</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Courses Results */}
                                    {(searchType === 'all' || searchType === 'courses') && (
                                        <div>
                                            <div className="flex items-center space-x-2 mb-3">
                                                <BookOpenIcon className="w-5 h-5 text-gray-600" />
                                                <h4 className="font-semibold text-gray-900">Courses</h4>
                                                {coursesLoading && <LoadingSpinner size="sm" />}
                                            </div>

                                            {courses?.data?.courses?.length > 0 ? (
                                                <div className="space-y-2">
                                                    {courses.data.courses.map((course) => (
                                                        <Link
                                                            key={course.id}
                                                            to={`/courses/${course.slug}`}
                                                            onClick={handleClose}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                                        >
                                                            <div className="flex items-start space-x-3">
                                                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                                    <BookOpenIcon className="w-5 h-5 text-green-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h5 className="font-medium text-gray-900 truncate">
                                                                        {course.title}
                                                                    </h5>
                                                                    <div className="flex items-center text-sm text-gray-500 mt-1">
                                                                        <AcademicCapIcon className="w-4 h-4 mr-1" />
                                                                        <span className="truncate">{course.universityId?.name}</span>
                                                                    </div>
                                                                    <div className="text-sm text-gray-600 mt-1">
                                                                        ₹{course.fee?.toLocaleString()} • {course.duration}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    ))}
                                                </div>
                                            ) : !coursesLoading && (
                                                <p className="text-gray-500 text-sm">No courses found</p>
                                            )}
                                        </div>
                                    )}

                                    {/* No Results */}
                                    {!universitiesLoading && !coursesLoading &&
                                        (!universities?.data?.universities?.length && !courses?.data?.courses?.length) && (
                                            <div className="text-center py-8">
                                                <MagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                <p className="text-gray-500">No results found for "{searchQuery}"</p>
                                                <p className="text-gray-400 text-sm mt-1">Try different keywords</p>
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>

                        {/* Quick Links */}
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="font-semibold text-gray-900 mb-3">Quick Links</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <Link
                                    to="/universities"
                                    onClick={handleClose}
                                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                >
                                    <AcademicCapIcon className="w-4 h-4 text-gray-600" />
                                    <span className="text-sm text-gray-700">All Universities</span>
                                </Link>
                                <Link
                                    to="/courses"
                                    onClick={handleClose}
                                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                >
                                    <BookOpenIcon className="w-4 h-4 text-gray-600" />
                                    <span className="text-sm text-gray-700">All Courses</span>
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </AnimatePresence>
    )
}

export default SearchModal
